import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '@beyond-js/ui/react';
import { config } from '@snapshots/config';
import { Links, type Storage } from '@snapshots/domain';
import { Address, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Outage, Problems } from '@snapshots/ui';

/**
 * A list with nothing in it yet (SNP-01), as the family's `EmptyState` with the way on: snapshots are made
 * in Packages. Inside a project it is a first run, with its `Checklist`: Packages enabled for the project
 * (an owner or admin is offered "Enable Packages for this project", in Projects where it is enabled; anyone
 * else is told who can), a snapshot made in Packages, then published on CDN. Deleted snapshots stay one step
 * away.
 */
export function Empty({ project, organization }: { project?: string; organization?: string }) {
	const { text } = usePresence();
	const { descriptor } = useFrame();
	const links = new Links(descriptor, { packages: config.packages });
	const deleted = (
		<Link to={`${Address.of({ project, organization })}${project || organization ? '&' : '?'}state=deleted`} className="text-ink underline">
			{text('captures.deleted')}
		</Link>
	);
	const { state, enable, where } = links.enablement;
	const packages = project && organization ? links.packages({ organization, project }) : links.entry('packages')?.url;
	const make = packages && state !== 'off' ? { label: text('unavailable.empty.action'), href: packages } : null;
	if (!project) {
		return (
			<div data-empty className="flex flex-col gap-3">
				<EmptyState kind="empty" title={text('captures.empty.title')} text={text('captures.empty.body')} action={make} />
				<p className="text-small">{deleted}</p>
			</div>
		);
	}
	const off = state === 'off';
	const steps = [
		{
			key: 'enable',
			title: text('first.enable'),
			state: off ? (enable ? ('current' as const) : ('blocked' as const)) : ('done' as const),
			reason: off && !enable ? text('first.enable.owner') : null,
			action: off && enable && where ? { label: text('unavailable.enable.action'), href: where } : null
		},
		{ key: 'make', title: text('first.make'), state: off ? ('todo' as const) : ('current' as const), action: make },
		{ key: 'publish', title: text('first.publish'), detail: text('first.publish.detail'), state: 'todo' as const }
	];
	return (
		<div data-empty={off ? 'enable' : ''} className="flex flex-col gap-3">
			<EmptyState
				kind={off ? 'context' : 'empty'}
				title={text(off ? 'unavailable.enable.title' : 'unavailable.empty.title')}
				text={text(off ? 'unavailable.enable.reason' : 'unavailable.empty.reason')}
				action={off ? (enable && where ? { label: text('unavailable.enable.action'), href: where } : null) : make}
				checklist={{ title: text('first.title'), steps }}
			/>
			<p className="text-small">{deleted}</p>
		</div>
	);
}

/**
 * The room an organization's snapshots have left (decision E9), which a line after the list says when it
 * nears its quota. It is read once for the organization, beside the list's own read: while the list is
 * unanswered, a read of it nobody answered joins the same line (D163) and is read again with it.
 */
export class Room {
	/** How much of each quota the organization uses, from 0; a quota of 0 counts as none used */
	static shares(storage: Storage): { snapshots: number; bytes: number } {
		const share = (used: number, quota: number) => (quota > 0 ? used / quota : 0);
		return { snapshots: share(storage.used.snapshots, storage.quota.snapshots), bytes: share(storage.used.bytes, storage.quota.bytes) };
	}

	/** "full" at either quota, "near" from 80 % of either, or null while there is room or nothing was read */
	static of(storage?: Storage): 'full' | 'near' | null {
		if (!storage) return null;
		const { snapshots, bytes } = Room.shares(storage);
		if (snapshots >= 1 || bytes >= 1) return 'full';
		return snapshots >= 0.8 || bytes >= 0.8 ? 'near' : null;
	}

	/**
	 * What the organization holds, for its line: `storage` once read for this organization, `outage` when
	 * nobody answered, and `read`, the same read again. A refusal says nothing: the line is only a hint.
	 */
	static use(organization?: string) {
		const { captures } = usePresence();
		const [held, setHeld] = useState<{ organization: string; storage?: Storage; outage?: Refusal }>();
		// Each organization's reading is its own: an answer for one never stands for another read after it
		const reading = useRef(0);
		const read = useCallback(async (): Promise<boolean> => {
			if (!organization) return true;
			const mine = reading.current;
			try {
				const storage = await captures.storage(organization);
				if (reading.current === mine) setHeld({ organization, storage });
				return true;
			} catch (cause) {
				const refusal = Problems.refusal(cause);
				const outage = Outage.cause(refusal) ? refusal : undefined;
				if (reading.current === mine) setHeld({ organization, outage });
				return !outage;
			}
		}, [captures, organization]);
		useEffect(() => {
			void read();
			return () => {
				reading.current += 1;
			};
		}, [read]);
		const own = held?.organization === organization ? held : undefined;
		return { storage: own?.storage, outage: own?.outage, read };
	}
}

/**
 * One line, only when an organization nears or reached its quota (decision E9): how much it keeps of what it
 * may, and the way to its storage. Nothing while it has room, or while what it holds is not known. The list
 * draws it after itself: it is known only once its own read answers, and arriving there it moves nothing
 * already drawn (D185).
 */
export function Quota({ storage }: { storage?: Storage }) {
	const { text, size } = usePresence();
	const room = Room.of(storage);
	if (!storage || !room) return null;
	const { snapshots, bytes } = Room.shares(storage);
	const said = room === 'full' ? text('quota.full') : snapshots >= bytes ? text('quota.near', { used: storage.used.snapshots, quota: storage.quota.snapshots }) : text('quota.storage', { used: size(storage.used.bytes), quota: size(storage.quota.bytes) });
	return (
		<p className="text-small text-ink-muted" data-quota={room}>
			{said}{' '}
			<Link to={`/storage?${new URLSearchParams({ organization: storage.organization })}`} className="text-ink underline">
				{text('quota.see')}
			</Link>
		</p>
	);
}
